import React, { useState } from 'react';
import { Copy, Check, FileText, Download, Shield } from 'lucide-react';
import {
  CISA_PILLARS,
  CISA_FUNCTIONS,
  MATURITY_CRITERIA,
  calculatePillarScore,
  getMaturityCriteria
} from '../../config/cisaMaturityModel';

/**
 * EvidenceSummary - Generates exportable leadership-ready summaries
 *
 * Props:
 * - assessments: { [functionCode]: maturityLevel }
 * - targetLevels: { [functionCode]: maturityLevel }
 * - productAdoptions: { [functionCode]: [{ productName, status }] }
 * - summary: { pillarScores, overallScore, levelCounts, ... }
 * - pillarFilter: string | null (filter to specific pillar)
 */
const EvidenceSummary = ({
  assessments = {},
  targetLevels = {},
  productAdoptions = {},
  summary = {},
  pillarFilter = null
}) => {
  const [copied, setCopied] = useState(false);

  // Format level name
  const formatLevel = (level) => level ? level.charAt(0) + level.slice(1).toLowerCase() : 'Traditional';

  // Get maturity level description
  const getLevelDescription = (level) => {
    switch (level) {
      case 'OPTIMAL':
        return 'Continuous, automated, and risk-adaptive controls with full integration across the enterprise.';
      case 'ADVANCED':
        return 'Automated controls with contextual awareness and dynamic policy enforcement.';
      case 'INITIAL':
        return 'Basic controls deployed with some automation and centralized management.';
      default:
        return 'Limited or no Zero Trust controls in place.';
    }
  };

  // Calculate overall maturity level from score
  const getOverallLevel = (score) => {
    if (score >= 3.5) return 'OPTIMAL';
    if (score >= 2.5) return 'ADVANCED';
    if (score >= 1.5) return 'INITIAL';
    return 'TRADITIONAL';
  };

  // Generate summary text
  const generateSummary = () => {
    const pillarsToInclude = pillarFilter
      ? CISA_PILLARS.filter(p => p.code === pillarFilter)
      : CISA_PILLARS;

    const overallLevel = getOverallLevel(summary.overallScore || 1.0);
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    let text = `CISA ZERO TRUST MATURITY ASSESSMENT\n`;
    text += `Generated: ${date}\n`;
    text += `${'='.repeat(50)}\n\n`;

    // Executive Summary
    text += `EXECUTIVE SUMMARY\n`;
    text += `${'-'.repeat(30)}\n`;
    text += `Overall Maturity Score: ${(summary.overallScore || 1.0).toFixed(1)} / 4.0\n`;
    text += `Current Maturity Level: ${formatLevel(overallLevel)}\n`;
    text += `\n${getLevelDescription(overallLevel)}\n\n`;

    // Key Metrics
    text += `KEY METRICS\n`;
    text += `${'-'.repeat(30)}\n`;
    text += `- Total Functions Assessed: ${summary.totalFunctions || 25}\n`;
    text += `- Functions with Gaps: ${summary.functionsWithGaps || 0}\n`;
    text += `- Products Tracked: ${summary.totalProductsAdopted || 0}\n\n`;

    // Level Distribution
    text += `MATURITY DISTRIBUTION\n`;
    text += `${'-'.repeat(30)}\n`;
    const levelCounts = summary.levelCounts || {};
    text += `- Optimal: ${levelCounts.OPTIMAL || 0} functions\n`;
    text += `- Advanced: ${levelCounts.ADVANCED || 0} functions\n`;
    text += `- Initial: ${levelCounts.INITIAL || 0} functions\n`;
    text += `- Traditional: ${levelCounts.TRADITIONAL || 0} functions\n\n`;

    // Pillar-by-Pillar Analysis
    text += `PILLAR ANALYSIS\n`;
    text += `${'='.repeat(50)}\n\n`;

    for (const pillar of pillarsToInclude) {
      const pillarScore = summary.pillarScores?.[pillar.code] || 1.0;
      const pillarLevel = getOverallLevel(pillarScore);
      const functions = CISA_FUNCTIONS[pillar.code] || [];

      text += `${pillar.name.toUpperCase()}\n`;
      text += `${'-'.repeat(30)}\n`;
      text += `Score: ${pillarScore.toFixed(1)} / 4.0 (${formatLevel(pillarLevel)})\n\n`;

      // Function details
      for (const fn of functions) {
        const currentLevel = assessments[fn.code] || 'TRADITIONAL';
        const target = targetLevels[fn.code];
        const products = productAdoptions[fn.code] || [];
        const inUseProducts = products.filter(p => p.status === 'IN_USE');

        text += `  ${fn.name}\n`;
        text += `  Current: ${formatLevel(currentLevel)}`;
        if (target && target !== currentLevel) {
          text += ` → Target: ${formatLevel(target)}`;
        }
        text += `\n`;

        // Show criteria for current level
        const criteria = getMaturityCriteria(fn.code, currentLevel);
        if (criteria) {
          text += `  ${criteria}\n`;
        }

        // Show products in use
        if (inUseProducts.length > 0) {
          text += `  Products: ${inUseProducts.map(p => p.productName).join(', ')}\n`;
        }

        text += `\n`;
      }

      text += `\n`;
    }

    // Recommendations Summary
    const functionsWithGaps = [];
    for (const pillar of pillarsToInclude) {
      const functions = CISA_FUNCTIONS[pillar.code] || [];
      for (const fn of functions) {
        const current = assessments[fn.code] || 'TRADITIONAL';
        const target = targetLevels[fn.code];
        if (target && target !== current) {
          const LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
          const gap = LEVELS.indexOf(target) - LEVELS.indexOf(current);
          if (gap > 0) {
            functionsWithGaps.push({
              name: fn.name,
              pillar: pillar.name,
              current,
              target,
              gap
            });
          }
        }
      }
    }

    if (functionsWithGaps.length > 0) {
      // Sort by gap size
      functionsWithGaps.sort((a, b) => b.gap - a.gap);

      text += `PRIORITY RECOMMENDATIONS\n`;
      text += `${'='.repeat(50)}\n\n`;

      for (const item of functionsWithGaps.slice(0, 5)) {
        text += `• ${item.name} (${item.pillar})\n`;
        text += `  ${formatLevel(item.current)} → ${formatLevel(item.target)} (${item.gap} level${item.gap > 1 ? 's' : ''} gap)\n\n`;
      }

      if (functionsWithGaps.length > 5) {
        text += `... and ${functionsWithGaps.length - 5} additional gaps identified.\n\n`;
      }
    }

    // Footer
    text += `${'='.repeat(50)}\n`;
    text += `Generated from CISA Zero Trust Maturity Model v2.0\n`;
    text += `Reference: https://www.cisa.gov/zero-trust-maturity-model\n`;

    return text;
  };

  // Copy to clipboard
  const handleCopy = async () => {
    const text = generateSummary();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Download as text file
  const handleDownload = () => {
    const text = generateSummary();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cisa-zt-assessment-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const overallLevel = getOverallLevel(summary.overallScore || 1.0);

  return (
    <div className="cisa-evidence-summary">
      <div className="cisa-evidence-summary-header">
        <div className="cisa-evidence-summary-title">
          <Shield size={20} className="cisa-evidence-summary-icon" />
          <div>
            <h3>Leadership Summary</h3>
            <p>Ready-to-share assessment report</p>
          </div>
        </div>

        <div className="cisa-evidence-summary-actions">
          <button
            type="button"
            className="cisa-evidence-summary-btn"
            onClick={handleCopy}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
          <button
            type="button"
            className="cisa-evidence-summary-btn cisa-evidence-summary-btn--primary"
            onClick={handleDownload}
          >
            <Download size={14} />
            Download Report
          </button>
        </div>
      </div>

      {/* Preview section */}
      <div className="cisa-evidence-summary-preview">
        <div className="cisa-evidence-summary-preview-header">
          <FileText size={16} />
          <span>Report Preview</span>
        </div>

        <div className="cisa-evidence-summary-card">
          <div className="cisa-evidence-summary-card-header">
            <span className="cisa-evidence-summary-card-title">
              CISA Zero Trust Maturity Assessment
            </span>
            <span className="cisa-evidence-summary-card-date">
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>

          <div className="cisa-evidence-summary-card-body">
            <div className="cisa-evidence-summary-metric">
              <span className="cisa-evidence-summary-metric-label">Overall Score</span>
              <span className="cisa-evidence-summary-metric-value">
                {(summary.overallScore || 1.0).toFixed(1)}
                <span className="cisa-evidence-summary-metric-max"> / 4.0</span>
              </span>
            </div>

            <div className="cisa-evidence-summary-metric">
              <span className="cisa-evidence-summary-metric-label">Maturity Level</span>
              <span className={`cisa-level-badge cisa-level--${overallLevel.toLowerCase()}`}>
                {formatLevel(overallLevel)}
              </span>
            </div>

            <p className="cisa-evidence-summary-description">
              {getLevelDescription(overallLevel)}
            </p>
          </div>

          <div className="cisa-evidence-summary-card-stats">
            <div className="cisa-evidence-summary-stat">
              <span className="cisa-evidence-summary-stat-value">{summary.totalFunctions || 25}</span>
              <span className="cisa-evidence-summary-stat-label">Functions Assessed</span>
            </div>
            <div className="cisa-evidence-summary-stat">
              <span className="cisa-evidence-summary-stat-value">{summary.functionsWithGaps || 0}</span>
              <span className="cisa-evidence-summary-stat-label">Active Gaps</span>
            </div>
            <div className="cisa-evidence-summary-stat">
              <span className="cisa-evidence-summary-stat-value">{summary.totalProductsAdopted || 0}</span>
              <span className="cisa-evidence-summary-stat-label">Products Tracked</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvidenceSummary;
